import * as React from "react";
import { Icon } from "../common";
import { auth } from "./firebase/firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from "firebase/auth";

interface AuthProvidersProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
  inline?: boolean;
  description?: string;
}

const AuthProviders: React.FC<AuthProvidersProps> = ({
  onSuccess,
  onCancel,
  showCancel = true,
  inline = true,
  description = "Sign in to access your dashboard",
}) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState<string>("");
  const [magicLinkSent, setMagicLinkSent] = React.useState<boolean>(false);
  const [showMagicLink, setShowMagicLink] = React.useState<boolean>(false);

  // Note: Magic link handling is now done in the /dashboard route
  // This prevents duplicate processing when AuthProviders is used elsewhere

  const handleGoogleSignIn = async () => {
    if (!auth) {
      setError("Authentication service not available");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      if (result.user) {
        onSuccess?.();
      }
    } catch (error: any) {
      setError("Failed to sign in with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!auth || !email.trim()) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const actionCodeSettings = {
        url: window.location.origin + "/dashboard",
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem("emailForSignIn", email);
      setMagicLinkSent(true);
    } catch (error: any) {
      setError("Failed to send magic link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${inline ? "max-w-md mx-auto" : ""}`}>
      <div className="bg-primary rounded-xl border border-secondary p-6">
        <div className="text-center mb-6">
          <p className="text-secondary text-sm">{description}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-3 border border-tertiary rounded-lg bg-secondary text-primary font-medium hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon icon="google" size={5} className="mr-3" />
            {loading ? "Signing in..." : "Continue with Google"}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-tertiary" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-primary text-secondary">or</span>
            </div>
          </div>

          {!showMagicLink ? (
            <button
              onClick={() => setShowMagicLink(true)}
              className="w-full flex items-center justify-center px-4 py-3 border border-tertiary rounded-lg bg-secondary text-primary font-medium hover:brightness-110 transition-all"
            >
              <svg
                className="w-5 h-5 mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                />
              </svg>
              Continue with Email
            </button>
          ) : (
            <div className="border border-tertiary rounded-lg p-4">
              {!magicLinkSent ? (
                <form onSubmit={handleMagicLinkSubmit}>
                  <div className="mb-3">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-primary mb-2"
                    >
                      Email address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full px-3 py-2 border border-tertiary rounded-md bg-secondary text-primary placeholder-secondary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                      required
                    />
                  </div>
                  <div className="mb-4 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300">
                    <span className="font-medium">Note:</span> Check your spam/junk folder if you don't see the email within a few minutes.
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={loading || !email.trim()}
                      className="flex-1 bg-accent !text-white py-2 px-4 rounded-md font-medium hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? "Sending..." : "Send Magic Link"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowMagicLink(false);
                        setMagicLinkSent(false);
                        setEmail("");
                      }}
                      className="px-3 py-2 text-secondary hover:text-primary transition-colors"
                    >
                      Back
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center">
                  <div className="mb-4">
                    <svg
                      className="w-12 h-12 text-accent mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <h4 className="text-lg font-semibold text-primary mb-2">
                      Check your email
                    </h4>
                    <p className="text-secondary text-sm mb-2">
                      We sent a magic link to <strong>{email}</strong>
                    </p>
                    <p className="text-secondary text-xs mb-3">
                      Click the link in your email to sign in.
                    </p>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-3">
                      <div className="flex items-start gap-2">
                        <svg
                          className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <div className="text-left">
                          <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                            Can't find the email?
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-300">
                            Check your spam/junk folder. The email comes from{" "}
                            <span className="font-mono font-semibold">
                              noreply@login.multiagentbook.com
                            </span>
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                            Please mark it as "Not Spam" if found there.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMagicLinkSent(false);
                      setEmail("");
                    }}
                    className="text-accent hover:text-accent/80 text-sm"
                  >
                    Use different email
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {showCancel && onCancel && (
          <div className="mt-4 text-center">
            <button
              onClick={onCancel}
              className="text-secondary hover:text-primary transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthProviders;
